import { useCallback } from 'react'

import type { ExternalIdentifier } from 'types/writer'

import { api } from '@disco/state-management/api/index'
import { useCreateRightsHolderMutation } from '@disco/state-management/api/rights-holders'

import { useDispatch } from 'hooks/use-redux'

import { useRightsNewModelEnabled } from './use-rights-new-model-enabled'

type RightsEntityType =
  | 'PRO'
  | 'Publisher'
  | 'Writer'
  | 'Rightsholder'
  | 'Label'

interface UseCreateRightsEntityOptions {
  /** The type of entity being created */
  entityType: RightsEntityType
  /** The old API thunk action to dispatch when feature flag is OFF */
  createThunk?: (
    name: string,
    externalIdentifiers?: ExternalIdentifier[],
  ) => any
}

/**
 * Hook for creating rights entities (PRO, Publisher, Writer)
 *
 * When rightsNewModelEnabled is ON, uses the new RTK Query mutation.
 * When OFF, uses the provided Redux thunk action.
 * When the migration is complete (SLAM-1413), update this hook to only
 * use the new API, then remove the feature flag check entirely.
 *
 * @example
 * ```tsx
 * const createEntity = useCreateRightsEntity({
 *   entityType: 'Writer',
 *   createThunk: createWriter,
 * })
 *
 * const handleSave = async (name, identifiers) => {
 *   const entityId = await createEntity(name, identifiers)
 *   // entityId is the created entity's ID
 * }
 * ```
 */
export function useCreateRightsEntity({
  entityType,
  createThunk,
}: UseCreateRightsEntityOptions) {
  const dispatch = useDispatch()
  const rightsNewModelEnabled = useRightsNewModelEnabled()
  const [createRightsHolder] = useCreateRightsHolderMutation()

  return useCallback(
    async (
      name: string,
      externalIdentifiers?: ExternalIdentifier[],
    ): Promise<number> => {
      if (rightsNewModelEnabled) {
        // Use new RTK Query API
        const result = await createRightsHolder({
          title: name,
          entity_type: entityType,
          external_identifiers: externalIdentifiers,
        }).unwrap()

        // Invalidate cache so new entity appears in dropdowns
        dispatch(
          api.util.invalidateTags([{ type: 'RightsHolders', id: 'LIST' }]),
        )

        return result.id
      } else {
        // Use old Redux thunk API
        if (createThunk) {
          const created = await dispatch(createThunk(name, externalIdentifiers))
          // Writers need explicit cache invalidation for the old API
          if (entityType === 'Writer') {
            dispatch(api.util.invalidateTags(['Writers']))
          }

          return created.id
        }
      }
    },
    [
      dispatch,
      rightsNewModelEnabled,
      createRightsHolder,
      entityType,
      createThunk,
    ],
  )
}
